import styles from 'css/src/components/app/recorder/VoiceoverPreview.module.scss';

import React, { useEffect, useRef, useState } from 'react';

import LottieAsset from 'js/src/components/common/ui/LottieAsset';

const RIPPLE_LOTTIE_PATH =
    'https://lottie.host/079e1290-1448-4f14-b43d-4af23ceafb6d/uvBH3CgYRA.json';

/** RMS level (0–1) above which we treat the input as sound rather than room noise. */
const SOUND_THRESHOLD = 0.02;
const BAR_COUNT = 72;
/** Skip the lowest bins, which mostly carry DC offset and mic rumble. */
const FIRST_FREQUENCY_BIN = 2;
/** `$color-primary-core` as RGB, so the halo can be drawn at varying opacity. */
const VISUALIZER_RGB = '86, 69, 245';

const getRmsLevel = (timeDomainData: Uint8Array) => {
    const sumOfSquares = timeDomainData.reduce((sum, sample) => {
        const normalized = (sample - 128) / 128;
        return sum + normalized * normalized;
    }, 0);
    return Math.sqrt(sumOfSquares / timeDomainData.length);
};

const resizeCanvasToDisplaySize = (canvas: HTMLCanvasElement) => {
    const pixelRatio = window.devicePixelRatio || 1;
    const width = Math.round(canvas.clientWidth * pixelRatio);
    const height = Math.round(canvas.clientHeight * pixelRatio);
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }
};

/**
 * Concentric halo around a pulsing core, ringed by frequency bars mirrored
 * left/right so the shape stays symmetric.
 */
const drawRadialVisualizer = (
    context: CanvasRenderingContext2D,
    frequencyData: Uint8Array,
    level: number,
) => {
    const { width, height } = context.canvas;
    const size = Math.min(width, height);
    const centerX = width / 2;
    const centerY = height / 2;
    const coreRadius = size * 0.14 * (1 + level * 0.35);
    const maxBarLength = size * 0.2;

    context.clearRect(0, 0, width, height);

    [3, 2, 1].forEach((ring) => {
        context.beginPath();
        context.arc(centerX, centerY, coreRadius * (1 + ring * 0.3 * level), 0, Math.PI * 2);
        context.fillStyle = `rgba(${VISUALIZER_RGB}, ${0.06 + 0.04 * (3 - ring)})`;
        context.fill();
    });

    context.beginPath();
    context.arc(centerX, centerY, coreRadius, 0, Math.PI * 2);
    context.fillStyle = `rgba(${VISUALIZER_RGB}, 0.25)`;
    context.fill();

    const barsPerSide = BAR_COUNT / 2;
    const usableBins = Math.min(frequencyData.length - FIRST_FREQUENCY_BIN, barsPerSide);
    context.strokeStyle = `rgb(${VISUALIZER_RGB})`;
    context.lineCap = 'round';
    context.lineWidth = Math.max(2, ((Math.PI * 2 * coreRadius) / BAR_COUNT) * 0.45);

    for (let bar = 0; bar < BAR_COUNT; bar++) {
        const distanceFromTop = bar < barsPerSide ? bar : BAR_COUNT - bar;
        const bin = FIRST_FREQUENCY_BIN + Math.min(distanceFromTop, usableBins - 1);
        const magnitude = frequencyData[bin] / 255;
        const barLength = size * 0.01 + magnitude * maxBarLength;
        const angle = (bar / BAR_COUNT) * Math.PI * 2 - Math.PI / 2;
        const startRadius = coreRadius + size * 0.02;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);

        context.beginPath();
        context.moveTo(centerX + cos * startRadius, centerY + sin * startRadius);
        context.lineTo(
            centerX + cos * (startRadius + barLength),
            centerY + sin * (startRadius + barLength),
        );
        context.stroke();
    }
};

const useAudioAnalyser = (stream: MediaStream | null | undefined) => {
    const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);

    useEffect(() => {
        if (!stream || stream.getAudioTracks().length === 0) {
            return undefined;
        }
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const node = audioContext.createAnalyser();
        node.fftSize = 256;
        node.smoothingTimeConstant = 0.75;
        source.connect(node);
        void audioContext.resume();
        setAnalyser(node);

        return () => {
            setAnalyser(null);
            source.disconnect();
            void audioContext.close();
        };
    }, [stream]);

    return analyser;
};

type MediaElementWithCapture = HTMLMediaElement & { captureStream?: () => MediaStream };

/**
 * Taps the playback element's output once it starts playing. `captureStream`
 * leaves playback itself untouched (unlike routing the element through Web
 * Audio, which would mute it if the AudioContext ended up suspended). Browsers
 * without it (Safari) just keep the idle ripple.
 *
 * When playback ends the browser ends the captured track, and a replay adds a
 * new track to the same captured stream, so we follow `addtrack` and hand back
 * a stream holding just the current track.
 */
const usePlaybackStream = (audioElement: MediaElementWithCapture | null) => {
    const [playbackStream, setPlaybackStream] = useState<MediaStream | null>(null);

    useEffect(() => {
        if (!audioElement?.captureStream) {
            return undefined;
        }
        let capturedStream: MediaStream | null = null;
        let analysedTrack: MediaStreamTrack | null = null;

        const analyseTrack = (track: MediaStreamTrack | undefined) => {
            if (track && track.kind === 'audio' && track !== analysedTrack) {
                analysedTrack = track;
                setPlaybackStream(new MediaStream([track]));
            }
        };
        const handleAddTrack = (event: MediaStreamTrackEvent) => analyseTrack(event.track);
        const captureOnPlay = () => {
            if (!capturedStream) {
                capturedStream = audioElement.captureStream?.() ?? null;
                capturedStream?.addEventListener('addtrack', handleAddTrack);
            }
            analyseTrack(capturedStream?.getAudioTracks()[0]);
        };

        audioElement.addEventListener('playing', captureOnPlay);
        return () => {
            audioElement.removeEventListener('playing', captureOnPlay);
            capturedStream?.removeEventListener('addtrack', handleAddTrack);
            setPlaybackStream(null);
        };
    }, [audioElement]);

    return playbackStream;
};

/**
 * Drives the canvas from the analyser on every animation frame, and latches
 * `hasSound` the first time the source carries sound — so it stays on the
 * visualizer through pauses — until the analyser or `sourceKey` changes.
 */
const useLiveAudioVisualizer = (
    analyser: AnalyserNode | null,
    canvasRef: React.RefObject<HTMLCanvasElement | null>,
    sourceKey: string | undefined,
) => {
    const [hasSound, setHasSound] = useState(false);

    useEffect(() => {
        setHasSound(false);
        if (!analyser) {
            return undefined;
        }
        const frequencyData = new Uint8Array(analyser.frequencyBinCount);
        const timeDomainData = new Uint8Array(analyser.fftSize);
        let animationFrameId = 0;
        let smoothedLevel = 0;
        let heardSound = false;

        const renderFrame = () => {
            analyser.getByteTimeDomainData(timeDomainData);
            analyser.getByteFrequencyData(frequencyData);

            const rmsLevel = getRmsLevel(timeDomainData);
            if (!heardSound && rmsLevel > SOUND_THRESHOLD) {
                heardSound = true;
                setHasSound(true);
            }

            smoothedLevel = smoothedLevel * 0.8 + Math.min(1, rmsLevel * 4) * 0.2;
            const canvas = canvasRef.current;
            const levelPercent = String(Math.round(smoothedLevel * 100));
            if (canvas && canvas.getAttribute('aria-valuenow') !== levelPercent) {
                canvas.setAttribute('aria-valuenow', levelPercent);
            }
            const context = canvas?.getContext('2d');
            if (canvas && context) {
                resizeCanvasToDisplaySize(canvas);
                drawRadialVisualizer(context, frequencyData, smoothedLevel);
            }
            animationFrameId = requestAnimationFrame(renderFrame);
        };
        animationFrameId = requestAnimationFrame(renderFrame);

        return () => cancelAnimationFrame(animationFrameId);
    }, [analyser, canvasRef, sourceKey]);

    return hasSound;
};

type VoiceoverPreviewProps = {
    /** The audio being recorded; once it carries sound, a live visualizer replaces the idle ripple. */
    liveStream?: MediaStream | null;
    /** Identifies the input feeding `liveStream`; changing it resets to the idle ripple. */
    inputDeviceId?: string;
    recordingUrl?: string;
};

/**
 * Center-stage visual for voiceover recording. A `VoiceoverRecorder` produces an
 * audio-only stream (no video track), so there is nothing to show in a `<video>`
 * preview. Until the input picks up sound we show an animated "ripple"; from
 * then on we show a radial visualizer driven by the live audio, so the user can
 * see their mic is working. When reviewing the finished recording, the same
 * visualizer follows the audio player's playback.
 */
const VoiceoverPreview: React.FC<VoiceoverPreviewProps> = ({
    liveStream,
    inputDeviceId,
    recordingUrl,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [playbackElement, setPlaybackElement] = useState<HTMLAudioElement | null>(null);
    const playbackStream = usePlaybackStream(playbackElement);
    const analyser = useAudioAnalyser(recordingUrl ? playbackStream : liveStream);
    const hasSound = useLiveAudioVisualizer(analyser, canvasRef, inputDeviceId);

    return (
        <div
            className={`${styles['voiceover-preview']} d-flex justify-content-center align-items-center`}
        >
            <div
                className={`${styles['layer']} ${styles['ripple']} ${hasSound ? styles['hidden'] : ''} d-flex justify-content-center align-items-center`}
                aria-hidden
            >
                <LottieAsset path={RIPPLE_LOTTIE_PATH} loop autoplay play />
            </div>
            {analyser && (
                <canvas
                    ref={canvasRef}
                    className={`${styles['layer']} ${hasSound ? '' : styles['hidden']}`}
                    role="meter"
                    aria-label="Audio level"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-hidden={!hasSound}
                />
            )}
            {recordingUrl && (
                <audio
                    ref={setPlaybackElement}
                    className={styles['playback']}
                    src={recordingUrl}
                    controls
                    autoPlay
                    controlsList="nodownload nofullscreen"
                    onContextMenu={(e) => e.preventDefault()}
                />
            )}
        </div>
    );
};

export default React.memo(VoiceoverPreview);
